import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Sparkles, X, ExternalLink, CheckCircle2, AlertTriangle, Film, RotateCw, Send, Newspaper } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PROVIDER_LABEL = { synthesia: "Synthesia", heygen: "HeyGen", did: "D-ID" };
const POLL_INTERVAL_MS = 8000;
const TERMINAL = new Set(["complete", "failed", "cancelled"]);

/**
 * Side-by-side comparison modal.
 *
 * Lives at the Avatar Studio page level — fires one script through all
 * selected providers in parallel, then polls until each job hits a
 * terminal status. Showcases each provider's render in a single
 * 3-column card so producers can A/B the look-and-feel without leaving
 * the studio.
 */
export default function CompareMode({ open, onClose, providers, defaultScript = "", defaultTitle = "" }) {
  const [step, setStep] = useState("compose"); // 'compose' | 'running'
  const [title, setTitle] = useState(defaultTitle);
  const [script, setScript] = useState(defaultScript);
  const [picked, setPicked] = useState(() => new Set(["synthesia", "heygen", "did"]));
  const [queueing, setQueueing] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [pollTick, setPollTick] = useState(0);

  useEffect(() => {
    if (open) {
      setStep("compose");
      setTitle(defaultTitle);
      setScript(defaultScript);
      setComparison(null);
    }
  }, [open, defaultScript, defaultTitle]);

  const toggle = (k) => {
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const connectedKeys = new Set((providers || []).filter((p) => p.connected).map((p) => p.provider));
  const eligible = Array.from(picked).filter((k) => connectedKeys.has(k));

  const startCompare = async () => {
    if (eligible.length < 2) {
      toast.error("Select at least 2 connected providers");
      return;
    }
    if (!script.trim()) { toast.error("Script is required"); return; }
    setQueueing(true);
    try {
      const r = await api.post("/avatar-studio/compare", {
        title: title || "Side-by-side comparison",
        script,
        providers: eligible,
      });
      setComparison(r.data);
      setStep("running");
      toast.success(`Queued ${eligible.length} renders — polling for completion`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not queue comparison");
    } finally { setQueueing(false); }
  };

  // Poll loop — only when in 'running' step and not every job terminal
  const refresh = useCallback(async () => {
    if (!comparison?.compare_id) return;
    try {
      const r = await api.get(`/avatar-studio/compare/${comparison.compare_id}`);
      setComparison((prev) => ({ ...prev, jobs: r.data.jobs }));
    } catch (e) { /* keep polling — transient */ }
  }, [comparison?.compare_id]);

  // Retry one or all failed providers — re-uses the original script + title
  const [retryingKey, setRetryingKey] = useState(""); // '' | 'all' | provider key
  const retry = useCallback(async (providers) => {
    if (!comparison?.compare_id) return;
    const labelKey = providers ? providers[0] : "all";
    setRetryingKey(labelKey);
    try {
      const r = await api.post(
        `/avatar-studio/compare/${comparison.compare_id}/retry`,
        providers ? { providers } : {},
      );
      setComparison((prev) => ({ ...prev, jobs: r.data.jobs }));
      const ok = (r.data.jobs || []).filter((j) =>
        r.data.retried.includes(j.provider) && j.ok).length;
      toast.success(`Retry queued · ${ok} of ${r.data.retried.length} re-queued cleanly`);
      // Kick the polling loop again
      setPollTick((t) => t + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally { setRetryingKey(""); }
  }, [comparison?.compare_id]);

  // Promote — open the destination picker for the chosen winning job
  const [promoteJob, setPromoteJob] = useState(null); // { provider, ...job }
  const openPromote = useCallback((job) => setPromoteJob(job), []);
  const closePromote = useCallback(() => setPromoteJob(null), []);

  useEffect(() => {
    if (step !== "running" || !comparison) return;
    const allTerminal = (comparison.jobs || []).every((j) =>
      !j.ok || TERMINAL.has(j.status));
    if (allTerminal) return;
    const id = setInterval(() => setPollTick((t) => t + 1), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [step, comparison]);

  useEffect(() => { if (pollTick > 0) refresh(); }, [pollTick, refresh]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="compare-modal"
      onClick={onClose}
    >
      <div
        className="bg-bg-surface border border-line rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-line">
          <Sparkles className="h-4 w-4 text-brand-amber" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-mono uppercase tracking-wider text-brand-amber">// SIDE-BY-SIDE COMPARE</div>
            <div className="text-sm text-ink-primary">
              {step === "compose"
                ? "Pick providers and a script — render in parallel"
                : `Running across ${eligible.length} providers`}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="compare-close"
            className="h-8 w-8 grid place-items-center rounded-md hover:bg-bg-elevated text-ink-secondary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {step === "compose" ? (
            <ComposeStep
              title={title} setTitle={setTitle}
              script={script} setScript={setScript}
              picked={picked} toggle={toggle}
              connectedKeys={connectedKeys}
              eligible={eligible}
              onStart={startCompare}
              queueing={queueing}
            />
          ) : (
            <ResultsGrid comparison={comparison} onRetry={retry} retryingKey={retryingKey} onPromote={openPromote} />
          )}
        </div>
      </div>

      {/* Promote destination picker (rendered as a sibling so it sits above the comparison modal) */}
      {promoteJob && (
        <PromoteModal
          compareId={comparison?.compare_id}
          job={promoteJob}
          comparisonTitle={comparison?.title || title}
          onClose={closePromote}
        />
      )}
    </div>
  );
}


function ComposeStep({ title, setTitle, script, setScript, picked, toggle, connectedKeys, eligible, onStart, queueing }) {
  const allCards = ["synthesia", "heygen", "did"];
  return (
    <div className="space-y-5" data-testid="compare-compose">
      <div>
        <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Title (optional)</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Q2 launch promo — provider bake-off"
          data-testid="compare-title-input"
          className="w-full mt-1 rounded-md bg-bg-elevated border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none"
        />
      </div>
      <div>
        <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Script (same script renders through every selected provider)</label>
        <textarea
          value={script}
          onChange={(e) => setScript(e.target.value)}
          rows={6}
          placeholder="Paste a 1-2 paragraph script…"
          data-testid="compare-script-input"
          className="w-full mt-1 rounded-md bg-bg-elevated border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none font-mono"
        />
        <div className="text-[10px] text-ink-muted text-right">{script.length} / 5000 chars</div>
      </div>
      <div>
        <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Providers (pick 2 or more connected)</label>
        <div className="grid grid-cols-3 gap-2 mt-2">
          {allCards.map((k) => {
            const connected = connectedKeys.has(k);
            const selected = picked.has(k);
            return (
              <button
                key={k}
                type="button"
                onClick={() => connected && toggle(k)}
                disabled={!connected}
                data-testid={`compare-provider-${k}`}
                className={`rounded-md border px-3 py-3 text-left transition disabled:opacity-50 disabled:cursor-not-allowed ${
                  !connected ? "border-line bg-bg-elevated" :
                  selected ? "border-brand-amber bg-brand-amber/10 ring-2 ring-brand-amber/40" :
                  "border-line bg-bg-surface hover:border-brand-amber/60"
                }`}
              >
                <div className="text-sm font-bold text-ink-primary">{PROVIDER_LABEL[k]}</div>
                <div className="text-[10px] text-ink-muted mt-1">
                  {connected ? (selected ? "✓ Included" : "Click to include") : "Not connected"}
                </div>
              </button>
            );
          })}
        </div>
        <div className="text-[11px] text-ink-muted mt-2">
          We auto-pick the first avatar (and voice for HeyGen / D-ID) for each provider so this is a one-click bake-off.
        </div>
      </div>
      <button
        onClick={onStart}
        disabled={queueing || eligible.length < 2 || !script.trim()}
        data-testid="compare-start-btn"
        className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-md text-sm font-bold bg-brand-amber text-bg disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {queueing
          ? <><Loader2 className="h-4 w-4 animate-spin" /> Queueing renders…</>
          : <><Sparkles className="h-4 w-4" /> Run side-by-side · {eligible.length} provider{eligible.length === 1 ? "" : "s"}</>}
      </button>
    </div>
  );
}


function ResultsGrid({ comparison, onRetry, retryingKey, onPromote }) {
  const jobs = comparison?.jobs || [];
  const failedJobs = jobs.filter((j) => !j.ok || j.status === "failed");
  const allDone = jobs.every((j) => !j.ok || ["complete", "failed", "cancelled"].includes(j.status));
  return (
    <div className="space-y-4" data-testid="compare-results">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-[11px] text-ink-muted flex-1">
          Comparison <code className="text-brand-amber font-mono">{comparison.compare_id}</code> · {jobs.length} job{jobs.length === 1 ? "" : "s"}{!allDone ? " · polling every 8s" : ""}
        </div>
        {failedJobs.length > 0 && allDone && (
          <button
            type="button"
            onClick={() => onRetry?.(null)}
            disabled={retryingKey === "all"}
            data-testid="compare-retry-all-btn"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-brand-amber/40 text-brand-amber hover:bg-brand-amber/10 disabled:opacity-60"
          >
            {retryingKey === "all"
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Retrying…</>
              : <><RotateCw className="h-3.5 w-3.5" /> Retry {failedJobs.length} failed</>}
          </button>
        )}
      </div>
      <div className={`grid gap-3 ${jobs.length >= 3 ? "grid-cols-3" : "grid-cols-2"}`}>
        {jobs.map((j) => (
          <JobCard
            key={j.provider}
            job={j}
            onRetry={onRetry}
            retrying={retryingKey === j.provider}
            onPromote={onPromote}
          />
        ))}
      </div>
    </div>
  );
}


function JobCard({ job, onRetry, retrying, onPromote }) {
  const label = PROVIDER_LABEL[job.provider] || job.provider;
  const isInflight = job.ok && !TERMINAL.has(job.status);
  const isRetryable = !job.ok || job.status === "failed";
  const isComplete = job.ok && job.status === "complete";
  return (
    <div
      className={`rounded-md border p-3 space-y-2 ${
        !job.ok ? "border-rose-500/30 bg-rose-500/5" :
        isComplete ? "border-emerald-500/30 bg-emerald-500/5" :
        job.status === "failed" ? "border-rose-500/30 bg-rose-500/5" :
        "border-line bg-bg-elevated"
      }`}
      data-testid={`compare-job-${job.provider}`}
    >
      <div className="flex items-center gap-2">
        <div className="text-sm font-bold text-ink-primary flex-1">{label}</div>
        {isInflight && <Loader2 className="h-4 w-4 animate-spin text-brand-amber" />}
        {isComplete && <CheckCircle2 className="h-4 w-4 text-emerald-300" />}
        {(!job.ok || job.status === "failed") && <AlertTriangle className="h-4 w-4 text-rose-400" />}
      </div>

      <div className="text-[10px] font-mono text-ink-muted">
        {job.ok ? `${job.status}${job.duration_sec ? ` · ${job.duration_sec}s` : ""}` : `error ${job.status || ""}`}
      </div>

      {/* Thumbnail or placeholder */}
      <div className="aspect-video rounded bg-bg-surface border border-line overflow-hidden grid place-items-center">
        {job.thumbnail_url ? (
          <img src={job.thumbnail_url} alt={label} className="w-full h-full object-cover" />
        ) : isComplete && job.download_url ? (
          <video src={job.download_url} controls className="w-full h-full object-cover" />
        ) : isInflight ? (
          <Loader2 className="h-5 w-5 animate-spin text-brand-amber" />
        ) : !job.ok ? (
          <div className="text-[10px] text-rose-300 text-center px-2">{job.error || "failed"}</div>
        ) : (
          <Film className="h-5 w-5 text-ink-muted" />
        )}
      </div>

      {isComplete && job.download_url && (
        <div className="flex gap-2">
          <a
            href={job.download_url}
            target="_blank"
            rel="noreferrer"
            data-testid={`compare-job-download-${job.provider}`}
            className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-bold py-1.5 rounded-md bg-bg-surface border border-line text-ink-primary hover:border-brand-amber/40"
          >
            <ExternalLink className="h-3 w-3" /> Open
          </a>
          <button
            type="button"
            onClick={() => onPromote?.(job)}
            data-testid={`compare-job-promote-${job.provider}`}
            className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-bold py-1.5 rounded-md bg-brand-amber text-bg"
          >
            <Send className="h-3 w-3" /> Promote
          </button>
        </div>
      )}
      {isRetryable && onRetry && (
        <button
          type="button"
          onClick={() => onRetry([job.provider])}
          disabled={retrying}
          data-testid={`compare-job-retry-${job.provider}`}
          className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold py-1.5 rounded-md border border-brand-amber/40 text-brand-amber hover:bg-brand-amber/10 disabled:opacity-60"
        >
          {retrying
            ? <><Loader2 className="h-3 w-3 animate-spin" /> Retrying…</>
            : <><RotateCw className="h-3 w-3" /> Retry {label}</>}
        </button>
      )}
      {job.ok && job.status === "failed" && !isRetryable && (
        <div className="text-[10px] text-rose-300 truncate">{job.error || "Render failed"}</div>
      )}
    </div>
  );
}


function PromoteModal({ compareId, job, comparisonTitle, onClose }) {
  const [title, setTitle] = useState(comparisonTitle || "");
  const [caption, setCaption] = useState("");
  const [wpEnabled, setWpEnabled] = useState(false);
  const [wpStatus, setWpStatus] = useState("draft");
  const [socialPicked, setSocialPicked] = useState(new Set());
  const [connections, setConnections] = useState({ wp: false, social: [] });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Probe which destinations are connected so we can pre-disable rows that
  // would be guaranteed to fail.
  useEffect(() => {
    (async () => {
      try {
        const [wpR, socialR] = await Promise.all([
          api.get("/blog-connector/wordpress/connection").catch(() => ({ data: null })),
          api.get("/social/status").catch(() => ({ data: { connections: [] } })),
        ]);
        const wpConn = !!(wpR.data && wpR.data.site_url);
        const social = (socialR.data?.connections || [])
          .filter((c) => c.connected)
          .map((c) => c.platform);
        setConnections({ wp: wpConn, social });
      } catch { /* probe failed — let the user try and we'll surface server errors */ }
    })();
  }, []);

  const toggleSocial = (p) => {
    setSocialPicked((s) => {
      const next = new Set(s);
      if (next.has(p)) next.delete(p); else next.add(p);
      return next;
    });
  };

  const submit = async () => {
    if (!wpEnabled && socialPicked.size === 0) {
      toast.error("Pick at least one destination");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/avatar-studio/compare/${compareId}/promote`, {
        provider: job.provider,
        caption,
        wordpress: {
          enabled: wpEnabled,
          title,
          status: wpStatus,
        },
        social_platforms: Array.from(socialPicked),
      });
      setResult(r.data);
      const wpOk = r.data.wordpress?.ok;
      const socOk = (r.data.social || []).filter((s) => s.ok).length;
      toast.success(`Promoted · ${wpOk ? "WP ✓" : ""}${wpOk && socOk ? " · " : ""}${socOk ? `${socOk} social drafts` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote failed");
    } finally { setSubmitting(false); }
  };

  const providerLabel = PROVIDER_LABEL[job.provider] || job.provider;
  const SOCIAL_PLATFORMS = ["facebook", "instagram", "x", "tiktok", "youtube", "threads", "linkedin"];

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="promote-modal"
      onClick={onClose}
    >
      <div
        className="bg-bg-surface border border-line rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-3 border-b border-line">
          <Send className="h-4 w-4 text-brand-amber" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-mono uppercase tracking-wider text-brand-amber">// PROMOTE WINNER</div>
            <div className="text-sm text-ink-primary">
              Push the <strong className="text-brand-amber">{providerLabel}</strong> render to your destinations.
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="promote-close"
            className="h-8 w-8 grid place-items-center rounded-md hover:bg-bg-elevated text-ink-secondary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {!result && (
            <>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Post title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  data-testid="promote-title-input"
                  className="w-full mt-1 rounded-md bg-bg-elevated border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Social caption (also used as WP excerpt)</label>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  rows={3}
                  placeholder="Defaults to the first 280 chars of the script."
                  data-testid="promote-caption-input"
                  className="w-full mt-1 rounded-md bg-bg-elevated border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none"
                />
              </div>

              {/* WordPress destination */}
              <div className="rounded-lg border border-line bg-bg-elevated p-3" data-testid="promote-wp-section">
                <div className="flex items-center gap-3">
                  <Newspaper className="h-4 w-4 text-ink-secondary" />
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-ink-primary">WordPress</div>
                    <div className="text-[10px] text-ink-muted">
                      {connections.wp ? "Connected — publishes immediately." : "Not connected · skip or connect from Blog Connector first."}
                    </div>
                  </div>
                  <label className="inline-flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={wpEnabled}
                      onChange={(e) => setWpEnabled(e.target.checked)}
                      disabled={!connections.wp}
                      data-testid="promote-wp-toggle"
                    />
                    Include
                  </label>
                </div>
                {wpEnabled && (
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-ink-secondary">
                    <span>Status:</span>
                    {["draft", "publish"].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setWpStatus(s)}
                        data-testid={`promote-wp-status-${s}`}
                        className={`px-2 py-1 rounded text-[10px] font-mono uppercase transition ${wpStatus === s ? "bg-brand-amber text-bg" : "bg-bg-surface border border-line text-ink-secondary hover:text-ink-primary"}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Social destinations */}
              <div className="rounded-lg border border-line bg-bg-elevated p-3" data-testid="promote-social-section">
                <div className="text-xs font-semibold text-ink-primary mb-2">Social drafts</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {SOCIAL_PLATFORMS.map((p) => {
                    const connected = connections.social.includes(p);
                    const selected = socialPicked.has(p);
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => connected && toggleSocial(p)}
                        disabled={!connected}
                        data-testid={`promote-social-${p}`}
                        className={`text-left text-xs px-3 py-2 rounded-md border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                          selected ? "border-brand-amber bg-brand-amber/10" :
                          connected ? "border-line bg-bg-surface hover:border-brand-amber/60" :
                          "border-line bg-bg-surface"
                        }`}
                      >
                        <div className="font-semibold capitalize">{p}</div>
                        <div className="text-[10px] text-ink-muted">
                          {connected ? (selected ? "✓ Draft will be queued" : "Click to include") : "Not connected"}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="text-[10px] text-ink-muted mt-2">
                  Social posts are queued as <strong className="text-ink-secondary">drafts</strong> in Social Publishing — review and fire from there.
                </div>
              </div>

              <button
                onClick={submit}
                disabled={submitting || (!wpEnabled && socialPicked.size === 0)}
                data-testid="promote-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-md text-sm font-bold bg-brand-amber text-bg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {(() => {
                  const total = (wpEnabled ? 1 : 0) + socialPicked.size;
                  if (submitting) return <><Loader2 className="h-4 w-4 animate-spin" /> Promoting…</>;
                  if (total === 0) return <><Send className="h-4 w-4" /> Pick at least one destination</>;
                  return <><Send className="h-4 w-4" /> Promote to {total} destination{total === 1 ? "" : "s"}</>;
                })()}
              </button>
            </>
          )}

          {/* Result panel */}
          {result && (
            <div className="space-y-3" data-testid="promote-results">
              <div className="text-xs font-mono uppercase tracking-wider text-brand-amber">// PROMOTE COMPLETE</div>
              {result.wordpress && (
                <div className={`rounded-md border p-3 ${result.wordpress.ok ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/30 bg-rose-500/5"}`}>
                  <div className="flex items-center gap-2 text-sm">
                    {result.wordpress.ok
                      ? <><CheckCircle2 className="h-4 w-4 text-emerald-300" /> WordPress · {result.wordpress.status}</>
                      : <><AlertTriangle className="h-4 w-4 text-rose-400" /> WordPress · {result.wordpress.error}</>}
                  </div>
                  {result.wordpress.link && (
                    <a href={result.wordpress.link} target="_blank" rel="noreferrer" className="text-[11px] text-brand-amber hover:underline mt-1 inline-flex items-center gap-1">
                      Open post <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              )}
              {(result.social || []).map((s, i) => (
                <div key={i} className={`rounded-md border p-3 ${s.ok ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/30 bg-rose-500/5"}`}>
                  <div className="flex items-center gap-2 text-sm">
                    {s.ok
                      ? <><CheckCircle2 className="h-4 w-4 text-emerald-300" /> <span className="capitalize">{s.platform}</span> · draft <code className="font-mono text-[10px] text-ink-muted">{s.post_id}</code> queued</>
                      : <><AlertTriangle className="h-4 w-4 text-rose-400" /> <span className="capitalize">{s.platform}</span> · {s.error}</>}
                  </div>
                </div>
              ))}
              <button onClick={onClose} data-testid="promote-done-btn" className="w-full py-2 rounded-md text-sm font-semibold border border-line text-ink-secondary hover:text-ink-primary">
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
